import React, { ReactNode, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { createPortal } from 'react-dom';
import { lighten } from 'polished';
import { colors, fontStyles } from '../../styles';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  fullScreen?: boolean;
  isDismissable?: boolean;
  maxWidth?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  fullScreen = false,
  isDismissable = false,
  maxWidth = '600px',
}) => {
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.getAttribute('data-theme') === 'dark');

  // Handle theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
    };

    handleThemeChange();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  // Prevent scrolling of background content when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  return createPortal(
    <div
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        overflow: hidden;
      `}
      onClick={(e) => {
        // Close modal when clicking on the backdrop (outside the modal)
        if (e.target === e.currentTarget && isDismissable) {
          onClose();
        }
      }}
    >
      <div
        css={css`
          ${/* background-color: ${isDarkMode ? colors.liquorice : colors.sugar}; */ ''}
          ${isDarkMode ? `background-color: ${lighten(0.1, colors.liquorice)};` : `background-color: ${colors.sugar};`}\
          border-radius: ${fullScreen ? '0' : '12px'};
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          width: ${fullScreen ? '100%' : '90%'};
          max-width: ${fullScreen ? '100%' : maxWidth};
          height: ${fullScreen ? '100%' : 'auto'};
          max-height: ${fullScreen ? '100%' : '90vh'};
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
          position: relative;
        `}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          css={css`
            background: none;
            border: none;
            cursor: pointer;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0;
          `}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
              fill={isDarkMode ? colors.sugar : colors.liquorice}
            />
          </svg>
        </button>
        {/* Title Container */}
        <div
          css={css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
          `}
        >
          {/* Title */}
          {title && (
            <h2
              css={css`
                ${fontStyles.dog};
                color: ${isDarkMode ? colors.sugar : colors.liquorice};
                margin: 0;
                padding: 0px 24px;
                text-align: center;
                flex: 1;
              `}
            >
              {title}
            </h2>
          )}
        </div>

        {/* Content */}
        <div
          css={css`
            flex: 1;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          `}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
